import { Country } from '../types';

export interface SEOConfig {
  default: {
    title: string;
    description: string;
    keywords: string[];
    image?: string;
  };
  country: {
    processing: string;
    editor: string;
    ready: string;
    selecting: string;
  };
  openGraph: {
    type: string;
    siteName: string;
    locale: {
      default: string;
      alternate: string[];
    };
  };
  twitter: {
    card: string;
    site?: string;
  };
}

export const seoConfig: SEOConfig = {
  default: {
    title: "Create Professional Passport Photos Online | Privacy-First AI Tool",
    description: "Generate official passport photos for any country with our privacy-first AI tool. Process photos locally on your device - never uploaded to servers. GDPR compliant and secure.",
    keywords: [
      "passport photo",
      "official photo", 
      "visa photo",
      "privacy",
      "AI photo editor",
      "local processing",
      "GDPR compliant",
      "secure photo tool",
      "passport size photo",
      "official document photo"
    ],
    image: process.env.REACT_APP_SEO_DEFAULT_IMAGE || "/og-image.jpg"
  },
  country: {
    processing: "Processing {country} Passport Photo | Secure & Local Processing",
    editor: "Edit Your {country} Passport Photo | Privacy-First Photo Tool",
    ready: "Your {country} Passport Photo is Ready | Download Official Photo",
    selecting: "Select Photo Area for {country} Passport | Privacy-First Tool"
  },
  openGraph: {
    type: "website",
    siteName: process.env.REACT_APP_SEO_SITE_NAME || "Privacy-First Passport Photos",
    locale: {
      default: "en_US",
      alternate: ["fi_FI"]
    }
  },
  twitter: {
    card: "summary_large_image",
    site: "@yoursite" // Update with actual Twitter handle
  }
};

// Helper function to generate country-specific SEO
export const getCountrySpecificSEO = (
  country: Country | null,
  state: 'default' | 'processing' | 'editor' | 'ready' | 'selecting',
  language: string = 'en'
) => {
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;
  const siteName = seoConfig.openGraph.siteName;
  
  if (!country) {
    return {
      title: seoConfig.default.title,
      description: seoConfig.default.description,
      keywords: seoConfig.default.keywords,
      canonical: baseUrl,
      image: `${baseUrl}${seoConfig.default.image}`
    };
  }

  const countryName = country.name;
  const countryCode = country.code.toLowerCase();
  
  let title: string;
  let description: string;
  let keywords: string[];

  switch (state) {
    case 'processing':
      title = seoConfig.country.processing.replace('{country}', countryName);
      description = `Currently processing your ${countryName} passport photo using AI. Photos are processed securely on your device - never sent to servers.`;
      keywords = [...seoConfig.default.keywords, `${countryName.toLowerCase()} passport`, `${countryCode} visa photo`];
      break;
      
    case 'editor':
      title = seoConfig.country.editor.replace('{country}', countryName);
      description = `Edit and adjust your ${countryName} passport photo area. Drag to resize and position your photo according to official ${countryName} requirements.`;
      keywords = [...seoConfig.default.keywords, `${countryName.toLowerCase()} photo editor`, `${countryCode} passport requirements`];
      break;
      
    case 'ready':
      title = seoConfig.country.ready.replace('{country}', countryName);
      description = `Your ${countryName} passport photo is ready for download. Generated according to official requirements and processed securely on your device.`;
      keywords = [...seoConfig.default.keywords, `${countryName.toLowerCase()} passport ready`, `download ${countryCode} photo`];
      break;
      
    case 'selecting':
      title = seoConfig.country.selecting.replace('{country}', countryName);
      description = `Select the photo area for your ${countryName} passport photo. Ensure proper face positioning according to official ${countryName} document requirements.`;
      keywords = [...seoConfig.default.keywords, `${countryName.toLowerCase()} photo selection`, `${countryCode} face positioning`];
      break;
      
    default:
      title = `Create Professional Passport Photos Online | MWQQ.com the Privacy-First AI Tool`;
      description = `Generate official passport photos for any country with our privacy-first AI tool. Process photos locally on your device - never uploaded to servers. GDPR compliant and secure.`;
      keywords = [
        "passport photo",
        "official photo",
        "visa photo",
        "privacy",
        "AI photo editor",
        "local processing",
        "GDPR compliant",
        "secure photo tool",
        "passport size photo",
        "official document photo"
      ];
  }

  return {
    title,
    description,
    keywords,
    // Canonical URL should always point to the primary version without query params
    // to avoid duplicate content issues
    canonical: baseUrl,
    image: `${baseUrl}${seoConfig.default.image}`
  };
};

// Language-specific URL helpers
export const getAlternateLanguageUrls = (country: Country | null) => {
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;
  const countryParam = country ? `?country=${country.code.toLowerCase()}` : '';
  
  return {
    'en': `${baseUrl}${countryParam}`,
    'fi': `${baseUrl}/fi${countryParam}`
  };
};